import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AppContext from 'Utils/AppContext';
import PageHeader from 'Utils/PageHeader';
import { BiSearchAlt } from 'react-icons/bi';
import { getDataSourceNumericId, filterJobStartable } from 'DataSources/datasourcesApi';
import SourcePicker, { RULES } from 'DataSources/SourcePicker';
import { startDiscoveryWithRule, listDiscoverJobsPage } from 'DataDiscovery/discoverApi';
import { listSamplingRuleTemplates } from 'DataDiscovery/samplingRulesApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import { startProfileJob } from 'Metadata/profileApi';
import { listDataSourceInventories } from 'Secure/api';

// The picker adapts its copy and CTA to the operation being started — reached from
// every landing page's Actions ▾ → "New X" item (data-sources.html mockup).
const ACTIONS = {
  discover: {
    title: 'New Discovery',
    pill: 'Discover',
    verb: 'Discover',
    subtitle: 'Choose a data source to scan for sensitive data.',
    back: '/discover',
  },
  profile: {
    title: 'New Fingerprint',
    pill: 'Fingerprint',
    verb: 'Fingerprint',
    subtitle: 'Choose a data source to fingerprint its schema and metadata.',
    back: '/metadata',
  },
  mask: {
    title: 'New Masking Job',
    pill: 'Mask',
    verb: 'Mask',
    subtitle: 'Choose a discovered data source to mask.',
    back: '/secure',
  },
  encrypt: {
    title: 'New Encryption Job',
    pill: 'Encrypt',
    verb: 'Encrypt',
    subtitle: 'Choose a discovered data source to encrypt.',
    back: '/secure',
  },
  default: {
    title: 'Select a data source',
    pill: 'New',
    verb: 'Select',
    subtitle: 'Choose a data source to run against.',
    back: '/',
  },
};

// Masking/encryption inventories are built from discovery results, so only sources with
// at least one discovery run or a saved inventory are valid targets. Returns the Set of
// numeric data-source ids that qualify: one paginated sweep of the discover-jobs list,
// then a per-source inventory check for the remainder only.
const JOBS_PAGE_CAP = 20;

async function fetchSecureEligibleIds(connections) {
  const ids = new Set();
  let page = 0;
  let totalPages = 1;
  while (page < totalPages && page < JOBS_PAGE_CAP) {
    // eslint-disable-next-line no-await-in-loop
    const { pageDto, discoverJobs } = await listDiscoverJobsPage(page, 200);
    totalPages = pageDto && pageDto.totalPages != null ? Number(pageDto.totalPages) : 1;
    discoverJobs.forEach((job) => {
      if (job && job.dataSourceId != null) ids.add(Number(job.dataSourceId));
    });
    page += 1;
  }
  const uncovered = connections.filter((c) => {
    const id = getDataSourceNumericId({ id: c.id });
    return id != null && !ids.has(id);
  });
  await Promise.all(
    uncovered.map(async (c) => {
      const id = getDataSourceNumericId({ id: c.id });
      const inventories = await listDataSourceInventories(id).catch(() => []);
      if (Array.isArray(inventories) && inventories.length > 0) ids.add(id);
    })
  );
  return ids;
}

function DataSourcePicker() {
  const history = useHistory();
  const location = useLocation();
  const { connections, setNotification } = useContext(AppContext);
  // Read-only accounts: SourcePicker disables the start itself (useCanEdit / READ_ONLY_TITLE).
  const [busyId, setBusyId] = useState(null);

  const actionKey = new URLSearchParams(location.search).get('action') || 'default';
  const action = ACTIONS[actionKey] || ACTIONS.default;
  const isSecureAction = actionKey === 'mask' || actionKey === 'encrypt';

  // null = still loading (mask/encrypt only); a fetch failure falls back to showing all
  // sources rather than blocking the flow on a transient error.
  const [secureEligibleIds, setSecureEligibleIds] = useState(null);
  const [secureEligibilityLoading, setSecureEligibilityLoading] = useState(false);

  useEffect(() => {
    if (!isSecureAction) return undefined;
    let cancelled = false;
    setSecureEligibilityLoading(true);
    fetchSecureEligibleIds(filterJobStartable(connections || []))
      .then((ids) => {
        if (cancelled) return;
        setSecureEligibleIds(ids);
        setSecureEligibilityLoading(false);
      })
      .catch((e) => {
        console.log('Could not load discovery/inventory eligibility for the picker', e);
        if (cancelled) return;
        setSecureEligibleIds(null);
        setSecureEligibilityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isSecureAction, connections]);

  // What each action may run on, as restrictions the picker shows and explains.
  const restrict = useMemo(() => {
    const rules = [RULES.jobStartable];
    if (!isSecureAction) return rules;
    // dynamodb supports neither masking (platform rejects with ENGINE_NOT_MASKABLE) nor
    // encryption; Salesforce is profile + discover only in v1 (platform NON_MASKABLE_ENGINES).
    rules.push(RULES.notEngine('dynamodb', 'DynamoDB cannot be masked or encrypted yet'), RULES.notSaas);
    if (actionKey === 'encrypt') {
      // mongodb is maskable (mongodb→mongodb) but encryption jobs still don't support it.
      rules.push(RULES.notEngine('mongodb', 'Encryption does not support MongoDB yet'));
      // Encryption is strictly in-place, so read-only (source-of-record) rows are locked.
      // Masking can run cross-source INTO a writable target, so read-only sources stay
      // pickable — the inventory page requires a target for them.
      rules.push(RULES.writable);
    }
    // Inventories are built from discovery results — only sources with a discovery run or a
    // saved inventory can be masked/encrypted. A failed eligibility fetch shows every source.
    if (secureEligibleIds) {
      rules.push(RULES.inSet(secureEligibleIds, 'Discovered or has an inventory',
        'No discovery run or saved inventory yet: run a discovery first'));
    }
    return rules;
  }, [actionKey, isSecureAction, secureEligibleIds]);

  // Sampling rules, for the discover action only. The Data Discovery page's New Discovery
  // started a run on the default rule with no way to choose one, while the source editor's
  // Run-Discovery caret let you pick — so the same operation offered the choice in one place
  // and not the other (C2D-1208). Loaded from the same endpoint the caret uses.
  const [rules, setRules] = useState([]);
  const [rulesLoaded, setRulesLoaded] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);

  useEffect(() => {
    if (actionKey !== 'discover') {
      setRulesLoaded(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await listSamplingRuleTemplates();
        if (cancelled) return;
        setRules(list);
        // Same rule the caret preselects: the server-seeded built-in, else the first row.
        const def = (Array.isArray(list) && (list.find((r) => r.builtIn === true) || list[0])) || null;
        if (def && def.id != null) setSelectedRuleId(def.id);
      } catch (e) {
        if (cancelled) return;
        // Non-fatal: startDiscoveryWithRule falls back to the default on a null rule, which
        // is exactly the old behaviour — the picker just cannot offer a choice.
        console.log('Failed to load sampling rules for the picker', e);
      } finally {
        if (!cancelled) setRulesLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [actionKey]);

  // Profile / discover start a job here; mask / encrypt / edit only navigate (the screen they
  // open is gated itself).
  const startsJob = actionKey === 'profile' || actionKey === 'discover';

  const run = async (connection) => {
    const id = getDataSourceNumericId({ id: connection.id, tableData: { id: connection.id } });
    if (id == null) return;
    if (actionKey === 'mask') {
      history.push(`/secure/mask/inventory/${encodeURIComponent(id)}`);
      return;
    }
    if (actionKey === 'encrypt') {
      history.push(`/secure/encrypt/inventory/${encodeURIComponent(id)}`);
      return;
    }
    setBusyId(connection.id);
    try {
      if (actionKey === 'profile' || actionKey === 'discover') {
        // Gate the start on a working connection; on failure the helper routes the
        // user to the source editor (navigation unmounts us, so no busy reset).
        const gate = await ensureConnectionTested(id, { history, setNotification, name: connection.name });
        if (!gate.ok) return;
      }
      if (actionKey === 'profile') {
        await startProfileJob(id);
        setNotification({ variant: 'success', message: `Fingerprint started for ${connection.name}.` });
        history.push('/metadata');
      } else if (actionKey === 'discover') {
        await startDiscoveryWithRule(id, selectedRuleId);
        const picked = rules.find((r) => r.id === selectedRuleId);
        setNotification({
          variant: 'success',
          message: picked
            ? `Discovery started for ${connection.name} using ${picked.name}.`
            : `Discovery started for ${connection.name}.`,
        });
        history.push('/discover');
      } else {
        history.push(`/sources/source-editor/${encodeURIComponent(connection.id)}`);
      }
    } catch (e) {
      console.log(`Picker ${actionKey} failed for ${connection.name}`, e);
      setNotification({
        variant: 'danger',
        message: `Could not start ${action.pill.toLowerCase()} for ${connection.name}: ${e.message || e}`,
      });
      // Success paths navigate away (component unmounts) — only reset here.
      setBusyId(null);
    }
  };

  const ruleSelect = actionKey === 'discover' && rulesLoaded && rules.length > 0 && (
    <label className='d-flex align-items-center mb-0' style={{ gap: 6 }}>
      <span className='sg-small' style={{ color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>Sampling rule</span>
      <select
        className='form-control form-control-sm'
        data-testid='picker-sampling-rule'
        value={selectedRuleId == null ? '' : String(selectedRuleId)}
        onChange={(e) => {
          const match = rules.find((r) => String(r.id) === e.target.value);
          setSelectedRuleId(match ? match.id : null);
        }}
        style={{ maxWidth: 240 }}
      >
        {rules.map((r) => (
          <option key={r.id} value={String(r.id)}>{r.name}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div>
      <PageHeader
        icon={BiSearchAlt}
        header={action.title}
        eyebrow={action.pill}
        tooltip={action.subtitle}
      />
      <p className='sg-small' style={{ color: 'var(--ink-500)' }}>{action.subtitle}</p>
      <SourcePicker
        layout='page'
        selection='single'
        sources={connections || []}
        restrict={restrict}
        verb={`${action.verb} →`}
        busy={busyId != null ? 'Starting…' : false}
        loading={isSecureAction && secureEligibilityLoading ? 'Checking discovery runs and inventories…' : null}
        toolbar={ruleSelect}
        onConfirm={(picked) => run(picked[0])}
        // Mask / encrypt only open the inventory page (gated itself); a read-only account may go there.
        readOnlyCanConfirm={!startsJob}
        onCancel={() => history.push(action.back)}
        testId='picker'
      />
    </div>
  );
}

export default DataSourcePicker;
export { ACTIONS };
